/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _13 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Введение</h2>
    <p className={styles.description}>
      Частая задача программирования - выполнение однотипного действия много раз. Например, вывести клиентов из списка
      один за другим, или перебрать суммы зарплат и для каждой выполнить одинаковый код. Именно для таких целей,
      многократного повторения одного участка кода, используются циклы.
    </p>
    <ul className={styles.list}>
      <li className={styles.list__item}>
        <b>Цикл</b> — управляющая конструкция в высокоуровневых языках программирования, предназначенная для организации
        многократного исполнения набора инструкций.
      </li>
      <li className={styles.list__item}>
        <b>Тело цикла</b> — последовательность инструкций, предназначенная для многократного исполнения.
      </li>
      <li className={styles.list__item}>
        <b>Итерация</b> — единичное выполнение тела цикла.
      </li>
      <li className={styles.list__item}>
        <b>Условие выхода</b> — выражение, определяющее будет в очередной раз выполняться итерация, или цикл завершится.
      </li>
      <li className={styles.list__item}>
        <b>Счётчик</b> — переменная, хранящая текущий номер итерации. Цикл не обязательно содержит счётчик, и он не
        обязан быть один, условие выхода из цикла может зависеть от нескольких изменяемых в цикле переменных.
      </li>
    </ul>
    <p className={styles.description}>Исполнение любого цикла включает:</p>
    <ul className={styles.list}>
      <li className={styles.list__item}>первоначальную инициализацию переменных цикла</li>
      <li className={styles.list__item}>проверку условия выхода</li>
      <li className={styles.list__item}>исполнение тела цикла</li>
      <li className={styles.list__item}>обновление переменной цикла на каждой итерации</li>
    </ul>
    <p className={styles.description}>
      Кроме того, большинство языков программирования предоставляют средства управления циклом, например, операторы
      завершения цикла, то есть выхода из цикла независимо от истинности условия выхода.
    </p>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>2 - Цикл for</h2>
      <p className={styles.description}>
        <b>Цикл со счётчиком</b> — цикл, в котором некоторая переменная изменяет своё значение от заданного начального
        до конечного значения с некоторым шагом и для каждого значения этой переменной тело цикла выполняется один раз.
      </p>
      <p className={styles.description}>
        В большинстве процедурных языков программирования реализуется оператором{' '}
        <code className={styles.code}>for</code>, в котором указывается счётчик, требуемое количество итераций и шаг, с
        которым изменяется счётчик.
      </p>
      <CopyBlock
        text={`for (initialization; condition; post-expression) {
  // statements
}`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />

      <p className={styles.description}>
        Алгоритм исполнения цикла <code className={styles.code}>for</code>:
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <strong>Инициализация (initialization)</strong> — выражение инициализации выполняется один раз, когда
          начинается цикл. Используется для инициализации переменной-счетчика. Если используется ключевое слово{' '}
          <code className={styles.code}>let</code>, переменная счетчика является локальной для цикла.
        </li>
        <li className={styles.list__item}>
          <strong>Условие (condition, test)</strong> — выражение, оцениваемое перед каждой итерацией цикла. Тело цикла
          выполняется только тогда, когда выражение условия принимает значение <code className={styles.code}>true</code>
          . Цикл завершается, если значение будет <code className={styles.code}>false</code>.
        </li>
        <li className={styles.list__item}>
          <strong>Тело (statements)</strong> — выполняется в случае удовлетворения условия.
        </li>
        <li className={styles.list__item}>
          <strong>Пост-выражение (post-expression)</strong> — выполняется после тела на каждой итерации цикла, но перед
          проверкой условия. Используется для обновления переменной-счетчика.
        </li>
      </ul>
      <p className={styles.description}>
        Переменные-счетчики, по традиции, называются буквами <code className={styles.code}>i/j/k</code>.
      </p>
      <CopyBlock
        text={`const max = 10;

for (let i = 0; i < max; i += 1) {
  console.log(i);
}`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />

      <p className={styles.description}>Посчитаем сумму чисел до определенного значения.</p>
      <CopyBlock
        text={`const target = 3;
let sum = 0;

for (let i = 0; i <= target; i += 1) {
  sum += i;
}

console.log(sum);`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />

      <p className={styles.description}>
        Вспомним про операцию <code className={styles.code}>a % b</code> и выведем остаток от деления используя цикл.
      </p>
      <CopyBlock
        text={`const max = 10;

for (let i = 0; i < max; i += 1) {
  console.log(\`\${max} % \${i} = \`, max % i);
}`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>3 - Инструкции break и continue</h2>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>3.1 - break</h3>
        <p className={styles.description}>
          Выйти из цикла можно не только при проверке условия, но и вообще в любой момент. Эту возможность предоставляет
          инструкция <code className={styles.code}>break</code>. Она полностью прекращает выполнение цикла и передаёт
          управление на строку за его телом.
        </p>
        <p className={styles.description}>Специально прервем цикл на 5-й итерации.</p>
        <CopyBlock
          text={`for (let i = 0; i < 10; i += 1) {
  if (i === 5) {
    console.log('Дошли до 5-й итерации, прерываем цикл!');
    break;
  }
}`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>3.2 - continue</h3>
        <p className={styles.description}>
          Директива <code className={styles.code}>continue</code> прерывает не весь цикл, а только выполнение текущей
          итерации. Её используют, если понятно, что на текущей итерации цикла делать больше нечего или вообще ничего
          делать не нужно и пора переходить на следующую итерацию.
        </p>
        <CopyBlock
          text={`/*
 * Используем цикл для вывода только нечетных чисел.
 * Для чётных i срабатывает continue, выполнение тела прекращается
 * и управление передаётся на следующую итерацию.
 */
const number = 10;

for (let i = 0; i < number; i += 1) {
  if (i % 2 === 0) {
    continue;
  }

  console.log('Нечетное i: ', i); // 1, 3, 5, 7, 9
}`}
          language="js"
          showLineNumbers={false}
          theme={ocean}
        />
      </section>
    </section>
  </section>
))
